import { useEffect, useState, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { getFirebaseDb } from '../services/firebase';
import {
  toggleActionItem,
  addActionItem as addActionItemService,
  deleteActionItem as deleteActionItemService,
  renameActionItem as renameActionItemService,
  reorderActionItems as reorderActionItemsService,
  deleteTaskPlan,
  normalizeTaskPlan,
  updateTaskSwimLane,
  updateTaskProject,
  updateTaskDueDate,
  setActionItemParent,
  createBulletTaskPlan,
  updateTaskTitle,
} from '../services/tasks';
import type { ActionItemStatus, TaskPlan, TodoSwimLane } from '../types';

interface UseTasksReturn {
  activeTasks: TaskPlan[];
  allTasks: TaskPlan[];
  loading: boolean;
  toggle: (taskId: string, actionItemId: string, completed: boolean) => Promise<void>;
  addItem: (
    taskId: string,
    text: string,
    options?: { parentItemId?: string | null; afterItemId?: string },
  ) => Promise<string | null>;
  deleteItem: (taskId: string, actionItemId: string) => Promise<void>;
  renameItem: (taskId: string, actionItemId: string, text: string) => Promise<void>;
  reorder: (
    taskId: string,
    orderedItemIds: string[],
    statusByItemId?: Partial<Record<string, ActionItemStatus>>,
    parentByItemId?: Partial<Record<string, string | null>>,
  ) => Promise<void>;
  setItemParent: (taskId: string, actionItemId: string, parentItemId: string | null) => Promise<void>;
  renameTask: (taskId: string, title: string) => Promise<void>;
  createBullet: (
    title: string,
    options?: { swimLane?: TodoSwimLane | null; projectId?: string | null },
  ) => Promise<string | null>;
  deleteTask: (taskId: string) => Promise<void>;
  setSwimLane: (taskId: string, lane: TodoSwimLane | null) => Promise<void>;
  setProject: (taskId: string, projectId: string | null) => Promise<void>;
  setDueDate: (taskId: string, dueDate: string | null) => Promise<void>;
}

export function useTasks(userId: string | null): UseTasksReturn {
  const [allTasks, setAllTasks] = useState<TaskPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    const q = query(
      collection(db, 'users', userId, 'tasks'),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setAllTasks(snap.docs.map((d) => normalizeTaskPlan(d.id, d.data())));
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [userId]);

  const activeTasks = allTasks.filter((t) => t.status === 'active');

  const toggle = useCallback(
    async (taskId: string, actionItemId: string, completed: boolean) => {
      if (!userId) return;
      await toggleActionItem(userId, taskId, actionItemId, completed);
    },
    [userId]
  );

  const addItem = useCallback(
    async (
      taskId: string,
      text: string,
      options?: { parentItemId?: string | null; afterItemId?: string },
    ) => {
      if (!userId) return null;
      return addActionItemService(userId, taskId, text, options);
    },
    [userId]
  );

  const deleteItem = useCallback(
    async (taskId: string, actionItemId: string) => {
      if (!userId) return;
      await deleteActionItemService(userId, taskId, actionItemId);
    },
    [userId]
  );

  const renameItem = useCallback(
    async (taskId: string, actionItemId: string, text: string) => {
      if (!userId) return;
      await renameActionItemService(userId, taskId, actionItemId, text);
    },
    [userId]
  );

  const reorder = useCallback(
    async (
      taskId: string,
      orderedItemIds: string[],
      statusByItemId?: Partial<Record<string, ActionItemStatus>>,
      parentByItemId?: Partial<Record<string, string | null>>,
    ) => {
      if (!userId) return;
      await reorderActionItemsService(userId, taskId, orderedItemIds, statusByItemId, parentByItemId);
    },
    [userId]
  );

  const setItemParent = useCallback(
    async (taskId: string, actionItemId: string, parentItemId: string | null) => {
      if (!userId) return;
      await setActionItemParent(userId, taskId, actionItemId, parentItemId);
    },
    [userId]
  );

  const renameTask = useCallback(
    async (taskId: string, title: string) => {
      if (!userId) return;
      await updateTaskTitle(userId, taskId, title);
    },
    [userId]
  );

  const createBullet = useCallback(
    async (
      title: string,
      options?: { swimLane?: TodoSwimLane | null; projectId?: string | null },
    ) => {
      if (!userId) return null;
      return createBulletTaskPlan(userId, title, options ?? {});
    },
    [userId]
  );

  const deleteTask = useCallback(
    async (taskId: string) => {
      if (!userId) return;
      await deleteTaskPlan(userId, taskId);
    },
    [userId]
  );

  const setSwimLane = useCallback(
    async (taskId: string, lane: TodoSwimLane | null) => {
      if (!userId) return;
      await updateTaskSwimLane(userId, taskId, lane);
    },
    [userId]
  );

  const setProject = useCallback(
    async (taskId: string, projectId: string | null) => {
      if (!userId) return;
      await updateTaskProject(userId, taskId, projectId);
    },
    [userId]
  );

  const setDueDate = useCallback(
    async (taskId: string, dueDate: string | null) => {
      if (!userId) return;
      await updateTaskDueDate(userId, taskId, dueDate);
    },
    [userId]
  );

  return {
    activeTasks,
    allTasks,
    loading,
    toggle,
    addItem,
    deleteItem,
    renameItem,
    reorder,
    setItemParent,
    renameTask,
    createBullet,
    deleteTask,
    setSwimLane,
    setProject,
    setDueDate,
  };
}

export function useActiveTasksForDate(userId: string | null): UseTasksReturn & {
  tasksForDate: (date: Date) => TaskPlan[];
} {
  const result = useTasks(userId);

  const tasksForDate = useCallback(
    (date: Date) => {
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      return result.allTasks.filter((task) => {
        const created = task.createdAt?.toDate?.()
          ?? new Date((task.createdAt as unknown as { seconds: number }).seconds * 1000);
        return created >= dayStart && created <= dayEnd;
      });
    },
    [result.allTasks]
  );

  return { ...result, tasksForDate };
}
